import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useNotification } from './member/NotificationProvider';
import { useLoginPrompt } from '../contexts/LoginPromptContext';

interface NavItemProps {
  to: string;
  icon: string;
  label: string;
  active: boolean;
  collapsed: boolean;
  isLocked?: boolean;
  onLockedClick?: (label: string) => void;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, active, collapsed, isLocked, onLockedClick }) => {
  const handleClick = (e: React.MouseEvent) => {
    if (isLocked) {
      e.preventDefault();
      onLockedClick?.(label);
    }
  };

  return (
    <Link
      to={isLocked ? '#' : to}
      onClick={handleClick}
      className={`flex items-center group relative px-3 py-2 rounded-lg transition-colors duration-150 ${
        active 
          ? 'bg-purple-600/20 text-purple-400 border-r-4 border-purple-600' 
          : isLocked
            ? 'text-slate-400 hover:bg-slate-800/80 hover:text-slate-200 cursor-pointer'
            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
      }`}
      title={collapsed ? (isLocked ? `${label} (Requer Login)` : label) : ''}
    >
      <div className={`flex items-center w-full ${collapsed ? 'justify-center relative' : 'space-x-3'}`}>
        <div className="relative flex items-center justify-center shrink-0">
          <i className={`fas ${icon} w-6 text-center text-base ${active ? 'text-purple-400' : isLocked ? 'text-slate-400 group-hover:text-slate-200' : 'group-hover:text-purple-400'}`}></i>
          {collapsed && isLocked && (
            <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-slate-700 text-slate-300 rounded-full flex items-center justify-center text-[7px] font-black ring-1 ring-slate-800">
              <i className="fas fa-lock text-[7px]"></i>
            </span>
          )}
        </div>
        {!collapsed && (
          <>
            <span className="font-medium text-sm flex-1 truncate">{label}</span>
            {isLocked && (
              <span className="ml-auto text-slate-500 group-hover:text-slate-300 flex items-center shrink-0 pl-1.5" title="Requer Login">
                <i className="fas fa-lock text-xs"></i>
              </span>
            )}
          </>
        )}
      </div>
    </Link>
  );
};

const SidebarGroup: React.FC<{ label: string; collapsed: boolean; children: React.ReactNode }> = ({ label, collapsed, children }) => (
  <div className="mb-4">
    {!collapsed && (
      <p className="px-4 text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2 mt-4 flex items-center">
        <span className="mr-2">{label}</span>
        <span className="flex-1 h-[1px] bg-slate-800/50"></span>
      </p>
    )}
    <div className="space-y-1">
      {children}
    </div>
  </div>
);

interface SidebarProps {
  collapsed: boolean;
  onMobileClose?: () => void;
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
  dbGames: any[];
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onMobileClose, activeGame, setActiveGame, dbGames }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { openLoginPrompt } = useLoginPrompt();
  const location = useLocation();
  const isGuest = !user;
  const [isGamePickerOpen, setIsGamePickerOpen] = useState(false);

  // Apenas as seguintes páginas são liberadas sem login:
  // início (/ ou /dashboard), suportes (/suporte), cartas (/busca), coleções (/colecoes), decks (/decks), ferramentas (/ferramentas), lojas (/lojas)
  const isGuestAllowed = (path: string) => {
    if (path === '/' || path === '/dashboard' || path === '/inicio') return true;
    if (path === '/suporte') return true;
    if (path === '/termos' || path.startsWith('/termos') || path === '/terms') return true;
    if (path === '/busca') return true;
    if (path === '/colecoes' || path.startsWith('/colecoes/')) return true;
    if (path === '/decks' || path.startsWith('/deck/')) return true;
    if (path === '/ferramentas') return true;
    if (path === '/como-jogar' || path.startsWith('/como-jogar/')) return true;
    if (path === '/regras' || path.startsWith('/regras/')) return true;
    if (path === '/lojas' || path.startsWith('/loja/')) return true;
    if (path === '/configuracoes' || path === '/settings') return true;
    return false;
  };

  const currentGameInfo = useMemo(() => {
    if (activeGame === 'All') return { label: 'Todos os Jogos' };
    const dbGame = dbGames.find(g => Number(g.id) === Number(activeGame));
    return { label: dbGame ? dbGame.name : 'Jogo selecionado' };
  }, [activeGame, dbGames]);

  // Member Specific Groups
  const memberGeral = [
    { to: isGuest ? '/dashboard' : '/', icon: 'fa-house', label: 'Início', active: location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/inicio' },
    { to: '/perfil', icon: 'fa-user-circle', label: 'Perfil', active: location.pathname === '/perfil' },
    { to: '/meus-decks', icon: 'fa-box-archive', label: 'Meus Decks', active: location.pathname === '/meus-decks' },
    { to: '/pastas', icon: 'fa-folder-open', label: 'Minhas Pastas', active: location.pathname === '/pastas' },
  ];

  const memberOutros = [
    { to: '/configuracoes', icon: 'fa-sliders', label: 'Configurações', active: location.pathname === '/configuracoes' || location.pathname === '/settings' },
    { to: '/suporte', icon: 'fa-circle-question', label: 'Suporte', active: location.pathname === '/suporte' },
    { to: '/termos', icon: 'fa-file-lines', label: 'Termos de Uso', active: location.pathname === '/termos' || location.pathname === '/termos-e-condicoes' || location.pathname === '/terms' },
  ];

  const memberColecao = [
    { to: '/busca', icon: 'fa-magnifying-glass', label: 'Cartas', active: location.pathname === '/busca' },
    { to: '/colecoes', icon: 'fa-layer-group', label: 'Coleções', active: location.pathname === '/colecoes' || location.pathname.startsWith('/colecoes/') },
    { to: '/como-jogar', icon: 'fa-book-open', label: 'Como Jogar', active: location.pathname.startsWith('/como-jogar') || location.pathname.startsWith('/regras') },
    { to: '/ferramentas', icon: 'fa-toolbox', label: 'Ferramentas', active: location.pathname === '/ferramentas' },
  ];

  const memberSocial = [
    { to: '/decks', icon: 'fa-table-list', label: 'Decks', active: location.pathname === '/decks' || location.pathname.startsWith('/deck/') },
    { to: '/comunidade', icon: 'fa-users', label: 'Membros', active: location.pathname === '/comunidade' },
    { to: '/meta', icon: 'fa-ranking-star', label: 'Meta', active: location.pathname === '/meta' },
    { to: '/reports', icon: 'fa-flag-checkered', label: 'Reports', active: location.pathname === '/reports' },
    { to: '/torneios', icon: 'fa-trophy', label: 'Torneios', active: location.pathname === '/torneios' || location.pathname.includes('/evento/') },
    { to: '/cardlink', icon: 'fa-right-left', label: 'Trocas', active: location.pathname === '/cardlink' || location.pathname === '/trocas' },
  ];

  const memberMercado = [
    { to: '/lojas', icon: 'fa-shop', label: 'Lojas', active: location.pathname === '/lojas' || location.pathname.startsWith('/loja/') },
    { to: '/produtos', icon: 'fa-bag-shopping', label: 'Produtos', active: location.pathname === '/produtos' },
    { to: '/ingressos', icon: 'fa-ticket', label: 'Ingressos', active: location.pathname === '/ingressos' || location.pathname === '/tickets' },
    { to: '/carrinho', icon: 'fa-shopping-cart', label: 'Carrinho', active: location.pathname === '/carrinho' },
    { to: '/pedidos', icon: 'fa-clipboard-list', label: 'Pedidos', active: location.pathname === '/pedidos' || location.pathname.startsWith('/pedido/') },
  ];

  return (
    <div className="flex flex-col h-full py-3">
      <div className={`px-5 py-4 flex items-center ${collapsed ? 'justify-center w-full' : 'justify-between'}`}>
        <Link to={isGuest ? "/dashboard" : "/"} className="flex items-center space-x-3 group">
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 px-2 py-1 rounded-lg group-hover:opacity-90 transition-opacity">
            <i className="fas fa-fish-fins text-white text-lg"></i>
          </div>
          {!collapsed && <span className="text-lg font-bold tracking-tight text-white group-hover:text-purple-300 transition-colors">Cardumy</span>}
        </Link>
        
        {onMobileClose && (
          <button onClick={onMobileClose} className="md:hidden text-slate-500 hover:text-white p-2">
            <i className="fas fa-xmark text-lg"></i>
          </button>
        )}
      </div>

      {/* Global Game Selector */}
      <div className={`px-4 mb-3 transition-opacity duration-150 ${collapsed ? 'md:hidden' : 'opacity-100'}`}>
        <div className="relative">
          <button 
            onClick={() => setIsGamePickerOpen(!isGamePickerOpen)}
            className="w-full bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 rounded-xl p-2.5 flex items-center justify-between transition-colors group"
          >
            <div className="flex items-center space-x-2.5">
              <div className="text-left">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 leading-none mb-1">Jogo em foco</p>
                <p className="text-xs font-bold text-white truncate max-w-[130px]">{currentGameInfo.label}</p>
              </div>
            </div>
            <i className="fas fa-chevron-down text-[10px] text-slate-500"></i>
          </button>

          {isGamePickerOpen && (
            <div className="absolute top-full left-0 right-0 mt-1.5 bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
              <div className="p-1.5 max-h-60 overflow-y-auto">
                <button 
                  onClick={() => { setActiveGame('All'); setIsGamePickerOpen(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-colors mb-0.5 flex items-center ${activeGame === 'All' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                >
                  <span>Ver Tudo</span>
                </button>
                {dbGames.map((game, idx) => {
                  const isGameAvailable = game.sauce === 'homura' || game.sauce === 'kyubey';
                  return (
                    <button 
                      key={game.id || `game-nav-${idx}`}
                      onClick={() => { 
                        if (!isGameAvailable) {
                          showNotification(`Estamos trabalhando na integração de ${game.name}. Em breve você poderá buscar cartas diretamente da API oficial!`, 'info');
                        }
                        setActiveGame(game.id); 
                        setIsGamePickerOpen(false); 
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-colors mb-0.5 flex items-center justify-between ${Number(activeGame) === Number(game.id) ? 'bg-purple-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                    >
                      <span>{game.name}</span>
                      {!isGameAvailable && (
                        <span className="text-[8px] bg-slate-950 px-1.5 py-0.5 rounded text-slate-500 font-black tracking-widest">BREVE</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 px-3 overflow-y-auto">
        <SidebarGroup label="Geral" collapsed={collapsed}>
          {memberGeral.map(item => (
            <NavItem
              key={item.to}
              {...item}
              collapsed={collapsed}
              isLocked={isGuest && !isGuestAllowed(item.to)}
              onLockedClick={(lbl) => openLoginPrompt(lbl)}
            />
          ))}
        </SidebarGroup>

        <SidebarGroup label="Jogos" collapsed={collapsed}>
          {memberColecao.map(item => (
            <NavItem
              key={item.to}
              {...item}
              collapsed={collapsed}
              isLocked={isGuest && !isGuestAllowed(item.to)}
              onLockedClick={(lbl) => openLoginPrompt(lbl)}
            />
          ))}
        </SidebarGroup>

        <SidebarGroup label="Comunidade" collapsed={collapsed}>
          {memberSocial.map(item => (
            <NavItem
              key={item.to}
              {...item}
              collapsed={collapsed}
              isLocked={isGuest && !isGuestAllowed(item.to)}
              onLockedClick={(lbl) => openLoginPrompt(lbl)}
            />
          ))}
        </SidebarGroup>

        <SidebarGroup label="Compras" collapsed={collapsed}>
          {memberMercado.map(item => (
            <NavItem
              key={item.to}
              {...item}
              collapsed={collapsed}
              isLocked={isGuest && !isGuestAllowed(item.to)}
              onLockedClick={(lbl) => openLoginPrompt(lbl)}
            />
          ))}
        </SidebarGroup>

        <SidebarGroup label="Outros" collapsed={collapsed}>
          {memberOutros.map(item => (
            <NavItem
              key={item.to}
              {...item}
              collapsed={collapsed}
              isLocked={isGuest && !isGuestAllowed(item.to)}
              onLockedClick={(lbl) => openLoginPrompt(lbl)}
            />
          ))}
        </SidebarGroup>
      </nav>

      {/* Social Links Sidebar */}
      {!collapsed && (
        <div className="px-4 py-2 border-t border-slate-800/60 flex items-center justify-around mt-auto">
          <a href="https://www.instagram.com/cardumy/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-pink-400 transition-colors p-1.5"><i className="fab fa-instagram"></i></a>
          <a href="https://chat.whatsapp.com/CdX5OCXmlojHTutlbjEqId" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-emerald-400 transition-colors p-1.5"><i className="fab fa-whatsapp"></i></a>
          <a href="https://discord.gg/hNWvmdz6ja" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-indigo-400 transition-colors p-1.5"><i className="fab fa-discord"></i></a>
        </div>
      )}
    </div>
  );
};
